// src/components/ambassadorStandingHistory/ambassadorStandingHistory.jsx
import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { standingLabelFormatter } from "../../constants/brandAmbassador";
import { apiGetAmbassadorLast30WorkedStanding } from "../../api/backend/brandAmbassador";
import "./ambassadorStandingHistory.scss";

export default function AmbassadorStandingHistory({ role, userId }) {
  const [standingData, setStandingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (role !== "brandAmbassador" || !userId) {
      // fallback week of perfect
      const today = new Date();
      setStandingData(
        Array.from({ length: 7 }, (_, i) => {
          const d = new Date(today);
          d.setDate(today.getDate() - (6 - i));
          return { performanceDate: d.toISOString().slice(0, 10), value: 3 };
        })
      );
      setLoading(false);
      return;
    }

    apiGetAmbassadorLast30WorkedStanding(userId)
      .then((res) => {
        const rows = res.data.standingHistory || [];
        const paddingCount = Math.max(0, 30 - rows.length);
        const padded = [
          ...Array.from({ length: paddingCount }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (30 - paddingCount - i));
            return { performanceDate: d.toISOString().slice(0, 10), value: 3 };
          }),
          ...rows.map((r) => ({
            performanceDate: r.performanceDate,
            value: r.value, // standingId 0–3
          })),
        ];
        setStandingData(padded.slice(-30));
      })
      .catch((err) => {
        console.error(err);
        setError(err);
      })
      .finally(() => setLoading(false));
  }, [role, userId]);

  if (role !== "brandAmbassador") return <p>No record found.</p>;
  if (loading) return <p>Loading standing history…</p>;
  if (error) return <p>Error loading standing history.</p>;

  return (
    <div className="ambassador-standing-history-container">
      <h2 className="standing-history-title">Standing History</h2>
      <p className="standing-history-subtitle">
        Your standing over the last 30 events.
      </p>
      <div className="standing-chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={standingData}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#444" />
            <XAxis
              dataKey="performanceDate"
              tickLine={false}
              stroke="#ccc"
              tickFormatter={(d, i) =>
                i === standingData.length - 1 ? "Latest" : d.slice(5)
              }
              tickCount={6}
            />
            <YAxis
              type="number"
              domain={[0, 3]}
              ticks={[0, 1, 2, 3]}
              tickLine={false}
              stroke="#ccc"
              tickFormatter={standingLabelFormatter}
            />
            <Tooltip
              formatter={(v) => [standingLabelFormatter(v), "Standing"]}
              contentStyle={{ backgroundColor: "#333", borderRadius: "8px" }}
              labelStyle={{ color: "#fff" }}
              itemStyle={{ color: "#fff" }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#e2b645"
              strokeWidth={3}
              // custom dot: only render on every 5th index
              dot={(props) => {
                const { cx, cy, index } = props;
                // show a dot every 5th point (adjust modulo as you like)
                if (index % 5 === 0) {
                  return (
                    <circle
                      key={`dot-${index}`}
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill="#e2b645"
                      stroke="#fff"
                      strokeWidth={1}
                    />
                  );
                }
                return null;
              }}
              // still show a bigger dot on hover
              activeDot={{ r: 6 }}
            />
            <ReferenceLine
              x={standingData[standingData.length - 1].performanceDate}
              stroke="#c79f27"
              strokeWidth={1} // extra thickness
              strokeDasharray="8 4" // longer red dashes
              isFront // ensures it renders above the grid
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
